/**
 * Trade page — what each kind of deal shows.
 *
 * A debt note, an equity offer, a fund, a commodity and an NGX stock all share one trade page,
 * but the figures that matter differ: a note is about its rate and maturity, a fund about its
 * unit price and rate, a stock or a metal about its price and how it has moved. These helpers
 * make that choice in one place, so the page, its cards and its drawers never disagree.
 */

import { hasOfferPeriod, hasSettledPricing, raiseProgressPercent } from "@/utils/offering";
import { compact, currencySymbol, money } from "@/utils/exchange.js";

export const DealKinds = Object.freeze({
  DEBT: "debt",
  EQUITY: "equity",
  BIDDING: "bidding",
  FUND: "fund",
  COMMODITY: "commodity",
  LISTED: "listed",
});

const DEBT_TYPES = ["Debt", "Fixed Interest", "Treasury"];

const DAY_MS = 24 * 60 * 60 * 1000;

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * @param {object} token
 * @param {object} [context]
 * @param {object} [context.ngx] the token's NGX listing, when it has one. Only the listing says a
 *   deal follows an NGX price: an IPO raising on GetEquity can carry the same "Stocks" category.
 */
export const dealKind = (token, { ngx } = {}) => {
  const type = token?.investment_type;
  if (type === "Commodity") return DealKinds.COMMODITY;
  if (type === "Fund") return DealKinds.FUND;
  if (DEBT_TYPES.includes(type)) return DealKinds.DEBT;
  if (ngx || token?.market?.kind === "ngx") return DealKinds.LISTED;
  if (hasOfferPeriod(token) && !hasSettledPricing(token)) return DealKinds.BIDDING;
  return DealKinds.EQUITY;
};

/** "share"/"shares", or "token"/"tokens" for a commodity, matching the trade drawer. */
export const unitWord = (token, count = 2) => {
  const word = dealKind(token) === DealKinds.COMMODITY ? "token" : "share";
  return num(count) === 1 ? word : `${word}s`;
};

/** What one unit trades at now: the last exchange price, else the offer price. */
export const unitPrice = (token) => num(token?.price?.exchange) || num(token?.price?.buy);

/** A fund's unit price moves in small steps, so it needs more places than a share price. */
export const priceDecimals = (token) =>
  dealKind(token) === DealKinds.FUND && unitPrice(token) < 10 ? 6 : 2;

/** Today's move as a fraction, against the price the nightly job recorded; null without one. */
export const dayChange = (token) => {
  const now = num(token?.price?.exchange);
  const prev = num(token?.prev_price?.exchange);
  return now > 0 && prev > 0 ? (now - prev) / prev : null;
};

/** "▲ 2.10%" / "▼ 0.40%" for a fraction, with its tone. */
export const formatMove = (fraction, suffix = "") => {
  if (fraction === null || fraction === undefined || !Number.isFinite(Number(fraction))) return null;
  const n = Number(fraction);
  const arrow = n > 0 ? "▲" : n < 0 ? "▼" : "";
  return {
    text: `${arrow} ${Math.abs(n * 100).toFixed(2)}%${suffix}`.trim(),
    tone: n > 0 ? "up" : n < 0 ? "down" : "",
  };
};

/**
 * One set of raise figures. The goal is the raise amount and everything else is derived from
 * the share of supply sold, so "raised", "left" and the percentage always add up, rather than
 * each being priced separately and disagreeing by a few kobo.
 */
export const raiseFigures = (token) => {
  const percent = raiseProgressPercent(token);
  const goal = num(token?.raise_amount) || num(token?.supply) * num(token?.price?.buy);
  const raised = token?.completed_raise ? goal : (goal * percent) / 100;
  return { goal, raised, left: Math.max(goal - raised, 0), percent };
};

/** Daily closes from the OHLVC feed, oldest first. */
export const closesFrom = (candles) =>
  (Array.isArray(candles) ? candles : [])
    .map((c) => ({
      date: new Date(c.date),
      value: num(typeof c.close === "object" ? c.close?.buy ?? c.close?.sell : c.close),
    }))
    .filter((p) => p.value > 0 && !Number.isNaN(p.date.getTime()))
    .sort((a, b) => a.date - b.date);

/** The move over the last `days`, or null when the history is shorter than that. */
export const periodReturn = (closes, days) => {
  if (closes.length < 2) return null;
  const last = closes[closes.length - 1];
  const cutoff = last.date.getTime() - days * DAY_MS;
  if (closes[0].date.getTime() > cutoff + 7 * DAY_MS) return null;
  const start = [...closes].reverse().find((p) => p.date.getTime() <= cutoff) || closes[0];
  return start.value > 0 ? (last.value - start.value) / start.value : null;
};

/** Lowest and highest close over the last year of history. */
export const yearRange = (closes) => {
  if (!closes.length) return null;
  const cutoff = closes[closes.length - 1].date.getTime() - 365 * DAY_MS;
  const values = closes.filter((p) => p.date.getTime() >= cutoff).map((p) => p.value);
  return { low: Math.min(...values), high: Math.max(...values) };
};

const shortDate = (value) =>
  new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** "14 days left", "1 day left", "closes today"; null once the date has passed. */
export const timeLeft = (value, now = Date.now()) => {
  const ms = new Date(value).getTime() - now;
  if (Number.isNaN(ms) || ms < 0) return null;
  const days = Math.floor(ms / DAY_MS);
  if (days >= 2) return `${days} days left`;
  return days === 1 ? "1 day left" : "closes today";
};

/**
 * The cards before Your Position, three at most, so the row never runs half empty.
 * Each card is { label, value, unit?, sub?, subTone?, tone?, note? }.
 *
 * @param {object} token
 * @param {object} context
 * @param {object} [context.book] the offering book while bids are open
 * @param {Array} [context.closes] daily closes, from closesFrom
 * @param {object} [context.commodity] the commodity details payload
 * @param {object} [context.ngx] the NGX listing payload
 * @param {string} [context.rateLabel] the deal's annual rate, as the page already formats it
 */
export const metricCards = (token, { book, closes = [], commodity, ngx, rateLabel } = {}) => {
  const currency = token?.currency || "USD";
  const symbol = currencySymbol(currency);
  const full = (value, decimals = 2) => money(value, currency, decimals);
  // Large figures read as ₦65.2T; the exact figure goes underneath.
  const big = (value) => (Math.abs(num(value)) >= 1e9 ? `${symbol}${compact(value)}` : full(value));
  const exactUnder = (value) => (Math.abs(num(value)) >= 1e9 ? full(value) : undefined);
  // ₦213m for a running total, where the exact naira says nothing more.
  const short = (value) => (Math.abs(num(value)) >= 1e6 ? `${symbol}${compact(value)}` : full(value, 0));
  const price = unitPrice(token);
  // A price that has not moved today says nothing, so the line only shows when it has.
  const change = dayChange(token);
  const today = change ? formatMove(change, " today") : null;
  const interest = num(token?.interest);

  const priceCard = (label) => ({
    label,
    value: full(price, priceDecimals(token)),
    sub: today?.text,
    subTone: today?.tone,
  });
  const interestCard = () => ({ label: "Interest Rate", value: `${rateLabel ?? interest}%`, unit: "p.a.", tone: "accent" });

  switch (dealKind(token, { ngx })) {
    case DealKinds.DEBT:
      return [
        { label: "Raise Amount", value: full(token.raise_amount) },
        interest > 0 && interestCard(),
        num(token.tenor) > 1 && {
          label: "Investment Period",
          value: `${token.tenor} days`,
          sub: token.maturity ? `matures ${shortDate(token.maturity)}` : undefined,
        },
      ].filter(Boolean);

    case DealKinds.BIDDING: {
      const guidance = token.price_guidance || {};
      const demand = num(book?.summary?.total_demand);
      return [
        { label: "Listing Valuation", value: big(token.valuation), sub: exactUnder(token.valuation) },
        guidance.floor && guidance.cap
          ? { label: "Price Range", value: `${full(guidance.floor)} – ${full(guidance.cap)}`, sub: "one price for all, set when bidding closes" }
          : priceCard("Share Price"),
        {
          label: "Bids So Far",
          value: book ? short(demand) : "—",
          sub: book
            ? `from ${num(book.summary?.commitment_count).toLocaleString("en-US")} ${num(book.summary?.commitment_count) === 1 ? "bid" : "bids"}`
            : token.offer_closes_at ? `bidding closes ${shortDate(token.offer_closes_at)}` : undefined,
        },
      ];
    }

    case DealKinds.FUND: {
      const issue = num(token.issue_price);
      const sinceLaunch = issue > 0 && price > 0 ? formatMove((price - issue) / issue, " since launch") : null;
      return [
        { label: "Unit Price", value: full(price, priceDecimals(token)), sub: sinceLaunch?.text, subTone: sinceLaunch?.tone },
        interest > 0
          ? { label: "Current Rate", value: `${interest}%`, unit: "a year", tone: "accent", sub: "added to the unit price daily" }
          : { label: "Pricing", value: "NAV", sub: "set by the fund manager" },
        // Nothing is paid out, and that is the thing people expect to work like a note.
        { label: "Payouts", value: "None", sub: "the return is added to the price daily" },
      ];
    }

    // Always in the deal's own currency: the spot at the rate the deal is priced at, and the
    // past year from the token's own history (backfilled from before it was listed).
    case DealKinds.COMMODITY: {
      const quote = commodity?.quote;
      const year = formatMove(periodReturn(closes, 365));
      const range = yearRange(closes);
      return [
        priceCard(`Price per ${unitWord(token, 1)}`),
        quote?.price && {
          label: `${commodity.name} ${commodity.price_basis === "front_month" ? "Future" : "Spot"}`,
          value: full(quote.price),
          unit: quote.quote_unit ? `per ${quote.quote_unit}` : undefined,
          sub: commodity.token_is ? `1 token = ${commodity.token_is}` : undefined,
        },
        year
          ? { label: "Past Year", value: year.text, tone: year.tone }
          : range && range.high > range.low && { label: "52-week Range", value: `${full(range.low)} – ${full(range.high)}` },
      ].filter(Boolean);
    }

    case DealKinds.LISTED: {
      const quote = ngx?.quote || {};
      const year = formatMove(periodReturn(closes, 365));
      const range = quote.low52wk && quote.high52wk ? { low: quote.low52wk, high: quote.high52wk } : yearRange(closes);
      const nearHigh = range && price > 0 && price >= range.high * 0.95;
      const nearLow = range && price > 0 && !nearHigh && price <= range.low * 1.05;
      const nearSub = nearHigh ? `near its 52-week high of ${full(range.high)}` : nearLow ? `near its 52-week low of ${full(range.low)}` : undefined;
      return [
        priceCard("Last Close"),
        quote.market_cap && {
          label: "Market Cap",
          value: big(quote.market_cap),
          sub: ngx.shares_outstanding ? `${compact(ngx.shares_outstanding)} shares in issue` : undefined,
        },
        year
          ? { label: "Past Year", value: year.text, tone: year.tone, sub: nearSub }
          : range && range.high > range.low && { label: "52-week Range", value: `${full(range.low)} – ${full(range.high)}` },
      ].filter(Boolean);
    }

    default: {
      const third = token.completed_raise && token.post_raise_valuation
        ? { label: "Secondary Valuation", value: big(token.post_raise_valuation), sub: exactUnder(token.post_raise_valuation) }
        : interest > 0
          ? interestCard()
          : token.offer_closes_at && !token.completed_raise
            ? { label: "Offer Closes", value: shortDate(token.offer_closes_at), sub: timeLeft(token.offer_closes_at) || "closed" }
            : !token.completed_raise && num(token.supply) > 0
              ? { label: "Shares on Offer", value: num(token.supply).toLocaleString("en-US", { maximumFractionDigits: 0 }) }
              : null;
      // While it raises, the price is the offer's, not a market's: there is no "today" move.
      const sharePrice = token.completed_raise
        ? priceCard("Share Price")
        : { label: "Share Price", value: full(price, priceDecimals(token)), sub: "fixed for this offer" };
      return [
        { label: "Listing Valuation", value: big(token.valuation), sub: exactUnder(token.valuation) },
        sharePrice,
        third,
      ].filter(Boolean);
    }
  }
};

/** Kinds whose price moves after issue, so a price chart says something. */
export const showsPriceChart = (token, context = {}) => {
  const kind = dealKind(token, context);
  return [DealKinds.LISTED, DealKinds.FUND, DealKinds.COMMODITY].includes(kind)
    || (kind === DealKinds.EQUITY && Boolean(token?.completed_raise && token?.secondaries));
};

/** Kinds that never chart, so their price history is not fetched. */
export const neverCharts = (token) => [DealKinds.DEBT, DealKinds.BIDDING].includes(dealKind(token));

/** Badge per kind, for every list: one label and one tone, so the pages agree. */
const BADGES = {
  [DealKinds.DEBT]: { label: "Debt", tone: "debt" },
  [DealKinds.FUND]: { label: "Fund", tone: "fund" },
  [DealKinds.COMMODITY]: { label: "Commodity", tone: "commodity" },
  [DealKinds.BIDDING]: { label: "Equity", tone: "equity" },
};

const pastMonth = (token) => {
  const series = Array.isArray(token?.historicals) ? token.historicals.map((p) => num(p.price ?? p.buy)).filter((v) => v > 0) : [];
  return series.length > 1 ? (series[series.length - 1] - series[0]) / series[0] : null;
};

/**
 * How a list card (Now Raising, the Exchange rows, the holdings) shows a deal: its badge, the
 * headline figure, three detail pills, the unit its quantity is in and the button. Reads only
 * what the list responses carry: the token, its `market` (an NGX listing or a metal) and its
 * 30-day `historicals`. Money is always in the deal's own currency.
 *
 * @param {object} token
 */
export const listingView = (token) => {
  const kind = dealKind(token);
  const market = token?.market || null;
  const currency = token?.currency || "USD";
  const full = (value, decimals = 2) => money(value, currency, decimals);
  const big = (value) => `${currencySymbol(currency)}${compact(value)}`;
  const price = unitPrice(token);
  const percentage = Number(token?.percentage);
  const change = Number.isFinite(percentage) && percentage !== 0
    ? formatMove(percentage / 100, " today")
    : formatMove(dayChange(token) || null, " today");
  const interest = num(token?.interest);
  const isSafe = token?.investment_type === "SAFE";
  const isEtf = market?.instrument === "etf";

  const badge = kind === DealKinds.LISTED
    ? { label: isEtf ? "NGX ETF" : "NGX Stock", tone: "ngx" }
    : BADGES[kind] || (isSafe ? { label: "SAFE", tone: "safe" } : { label: token?.investment_type || "Equity", tone: "equity" });

  const view = { kind, badge, change, unit: unitWord(token), cta: "Invest Now", hero: null, pills: [] };

  switch (kind) {
    case DealKinds.DEBT: {
      const pays = token.payout_frequency ? `paid ${String(token.payout_frequency).toLowerCase()}` : "paid at maturity";
      view.hero = interest > 0 ? { value: `${interest}%`, unit: `annual yield · ${pays}` } : { value: full(price), unit: "per share" };
      view.pills = [
        interest > 0 && { k: "Interest", v: `${interest}%`, tone: "up" },
        num(token.tenor) > 0 && { k: "Tenor", v: `${token.tenor}d` },
        token.payout_frequency && { k: "Payout", v: token.payout_frequency },
      ].filter(Boolean);
      view.change = null;
      break;
    }
    case DealKinds.FUND:
      view.hero = interest > 0
        ? { value: `${interest}%`, unit: "a year, built into the unit price" }
        : { value: full(price, priceDecimals(token)), unit: "per unit, at net asset value" };
      view.pills = [
        { k: "Unit price", v: full(price, priceDecimals(token)) },
        interest > 0 ? { k: "Rate", v: `${interest}% a year`, tone: "up" } : { k: "Priced", v: "At NAV" },
        { k: "Payouts", v: "In the price" },
      ];
      break;
    case DealKinds.COMMODITY: {
      const per = market?.unit_amount === 1 && market?.unit_label ? market.unit_label : "token";
      const month = formatMove(pastMonth(token));
      view.hero = { value: full(price), unit: `per ${per}` };
      view.pills = [
        market?.token_is && { k: "1 token", v: market.token_is },
        month && { k: "Past month", v: month.text, tone: month.tone },
        { k: "Priced", v: "Weekdays" },
      ].filter(Boolean);
      view.cta = `Buy ${market?.name || token.name}`;
      break;
    }
    case DealKinds.LISTED: {
      const q = market?.quote || {};
      view.hero = { value: full(price), unit: "per share at the NGX close" };
      view.pills = [
        q.last_updated && { k: "NGX close", v: new Date(q.last_updated).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) },
        q.low52wk && q.high52wk && { k: "52-wk", v: `${full(q.low52wk)} – ${full(q.high52wk)}` },
        q.market_cap && { k: "Mkt cap", v: big(q.market_cap) },
      ].filter(Boolean);
      view.cta = "Buy Shares";
      break;
    }
    case DealKinds.BIDDING: {
      const g = token.price_guidance || {};
      view.hero = g.floor && g.cap ? { value: `${full(g.floor)} – ${full(g.cap)}`, unit: "per share, set when bidding closes" } : { value: full(price), unit: "per share" };
      view.pills = [num(token.valuation) > 0 && { k: "Valuation", v: big(token.valuation) }].filter(Boolean);
      view.change = null;
      break;
    }
    default:
      view.hero = isSafe && num(token.valuation) > 0
        ? { value: big(token.valuation), unit: "valuation cap" }
        : { value: full(price), unit: "per share" };
      view.pills = [
        num(token.valuation) > 0 && { k: isSafe ? "Valuation cap" : "Valuation", v: big(token.valuation) },
        num(token.total_supply) > 0 && { k: "Shares", v: compact(token.total_supply) },
      ].filter(Boolean);
  }
  return view;
};

/**
 * A number of tokens in the unit they stand for: a metal in its own unit (tokens × the metal per
 * token, "120 grams"), everything else as shares. `market` is the list responses' `market`, or
 * on the trade page the commodity details, which carry the same unit fields.
 *
 * @param {object} token
 * @param {number} units
 * @param {{ market?: object, short?: boolean }} [options] - short gives "39.9K grams"
 */
export const unitsLabel = (token, units, { market = token?.market, short = false } = {}) => {
  const format = (n) => (short ? compact(n) : n.toLocaleString("en-US", { maximumFractionDigits: 2 }));
  if (dealKind(token) === DealKinds.COMMODITY && market?.unit_label) {
    const amount = num(units) * num(market.unit_amount || 1);
    const label = market.unit_label;
    // "board feet", "MMBtu": the backend says when a unit is not its label and an s.
    const plural = market.unit_plural || (/s$/.test(label) ? label : `${label}s`);
    return `${format(amount)} ${amount === 1 ? label : plural}`;
  }
  return `${format(num(units))} ${unitWord(token, num(units))}`;
};

/** A holding's quantity in the unit it is held in. */
export const holdingQuantity = (token) => unitsLabel(token, token?.balance);

/** Tokens still on offer: what the raise has left, or once it has closed, what it offered. */
export const remainingUnits = (token) => (
  token?.completed_raise ? num(token?.supply) : Math.max(num(token?.supply) - num(token?.total_raised), 0)
);

/** What is still on offer, short, in the unit it is bought in ("39.9K grams", "1.4K shares"). */
export const availableLabel = (token, options = {}) => unitsLabel(token, remainingUnits(token), { short: true, ...options });

/**
 * Metals and NGX stocks are not raises with a goal to reach: investors buy at the market price
 * and the custodian buys what they bought. They show what is available, not how far a target is.
 *
 * @param {object} token
 * @param {object} [context] - see dealKind
 */
export const sellsByAvailability = (token, context = {}) => (
  [DealKinds.COMMODITY, DealKinds.LISTED].includes(dealKind(token, context))
);
